import React from 'react';
import { Link } from 'react-router-dom';
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ExitToAppRounded as ExitToAppIcon } from '@material-ui/icons';

import { TableBodySkeleton } from '../../../shared/components/Tables/TableBodySkeleton.component';
import { ROWS_PER_PAGE_OPTIONS } from '../../../shared/constants/table.constants';
import { formatPrice } from '../../../shared/utils/format-price.util';
import { formatShortDate } from '../../../shared/utils/format-date.util';
import { ADMIN_USERS_APP_PATHS } from '../../../users/constants/users-paths.constants';
import { ADMIN_INVITATIONS_APP_PATHS } from '../../constants/invitations-app-paths.constants';
import { INVITATIONS_LIST_COLUMNS } from '../../constants/invitations-table.constants';
import {
  InvitationColumn,
  InvitationRow,
  InvitationSortColumn,
  InvitationSortDirection,
} from '../../types/invitation.type';

interface InvitationsTableHeader {
  label: string;
  minWidth: number;
  align: 'left' | 'right' | 'center';
  sortColumn?: InvitationSortColumn;
}

interface InvitationsTableProps {
  invitations: InvitationRow[];
  totalCount: number;
  page: number;
  rowsPerPage: number;
  isLoading: boolean;
  sortBy: InvitationSortColumn;
  sortDirection: InvitationSortDirection;
  columns?: InvitationColumn[];
  emptyLabel?: string;
  maxHeight?: string;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onSortChange: (column: InvitationSortColumn) => void;
}

const TABLE_HEADERS: Record<InvitationColumn, InvitationsTableHeader> = {
  sender: { label: 'Éco-acteur', minWidth: 200, align: 'left' },
  participants: { label: 'Participants', minWidth: 110, align: 'center' },
  totalPaid: { label: 'Total payé', minWidth: 110, align: 'right' },
  createdAt: { label: 'Créée le', minWidth: 110, align: 'left', sortColumn: 'createdAt' },
  deliveryDate: { label: 'Livraison le', minWidth: 140, align: 'left', sortColumn: 'deliveryDate' },
};

const useStyles = makeStyles((theme) => ({
  link: {
    color: theme.palette.admin.primary.main,
    textDecoration: 'none',

    '&:hover': {
      textDecoration: 'underline',
    },
  },
  deletedSender: {
    color: '#757575',
    fontStyle: 'italic',
  },
  secondaryLine: {
    display: 'block',
    fontSize: 12,
    color: '#757575',
  },
}));

function InvitationsTableHeaderCell({
  header,
  sortBy,
  sortDirection,
  onSortChange,
}: {
  header: InvitationsTableHeader;
  sortBy: InvitationSortColumn;
  sortDirection: InvitationSortDirection;
  onSortChange: (column: InvitationSortColumn) => void;
}) {
  const { sortColumn } = header;
  const isSorted = sortColumn === sortBy;

  return (
    <TableCell
      align={header.align}
      sortDirection={isSorted ? sortDirection : false}
      style={{ minWidth: header.minWidth, fontWeight: 'bold' }}
    >
      {sortColumn ? (
        <TableSortLabel
          active={isSorted}
          direction={isSorted ? sortDirection : 'desc'}
          onClick={() => onSortChange(sortColumn)}
        >
          {header.label}
        </TableSortLabel>
      ) : (
        header.label
      )}
    </TableCell>
  );
}

const renderCellContent = (
  column: InvitationColumn,
  invitation: InvitationRow,
  classes: ReturnType<typeof useStyles>
): React.ReactNode => {
  const { sender } = invitation;

  switch (column) {
    case 'sender':
      return sender ? (
        <Link className={classes.link} to={ADMIN_USERS_APP_PATHS.USER_DETAILS(sender.id)}>
          {sender.firstName} {sender.lastName}
        </Link>
      ) : (
        <span className={classes.deletedSender}>Compte supprimé</span>
      );
    case 'participants':
      return `${invitation.paidOrdersCount} / ${invitation.contactsCount}`;
    case 'totalPaid':
      return formatPrice(invitation.totalPaid);
    case 'createdAt':
      return formatShortDate(invitation.createdAt);
    case 'deliveryDate':
      return (
        <>
          {invitation.deliveryDate ? formatShortDate(invitation.deliveryDate) : '-'}
          {invitation.deliverySchedule && <span className={classes.secondaryLine}>{invitation.deliverySchedule}</span>}
        </>
      );
  }
};

function InvitationTableRow({ invitation, columns }: { invitation: InvitationRow; columns: InvitationColumn[] }) {
  const classes = useStyles();

  return (
    <TableRow hover tabIndex={-1}>
      <TableCell align="center">
        <IconButton
          size="small"
          aria-label="voir"
          component={Link}
          to={ADMIN_INVITATIONS_APP_PATHS.INVITATION_DETAILS(invitation.id)}
        >
          <ExitToAppIcon />
        </IconButton>
      </TableCell>
      {columns.map((column) => (
        <TableCell key={column} align={TABLE_HEADERS[column].align}>
          {renderCellContent(column, invitation, classes)}
        </TableCell>
      ))}
    </TableRow>
  );
}

export function InvitationsTable({
  invitations,
  totalCount,
  page,
  rowsPerPage,
  isLoading,
  sortBy,
  sortDirection,
  columns = INVITATIONS_LIST_COLUMNS,
  emptyLabel = 'Aucune invitation trouvée',
  maxHeight = '75svh',
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
}: InvitationsTableProps) {
  const columnCount = columns.length + 1;

  return (
    <Paper>
      <TableContainer style={{ maxHeight }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell aria-label="Actions" align="center" style={{ minWidth: 50 }} />
              {columns.map((column) => (
                <InvitationsTableHeaderCell
                  key={column}
                  header={TABLE_HEADERS[column]}
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSortChange={onSortChange}
                />
              ))}
            </TableRow>
          </TableHead>
          {isLoading && <TableBodySkeleton rows={rowsPerPage} columns={columnCount} />}
          {!isLoading && (
            <TableBody>
              {invitations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columnCount} style={{ textAlign: 'center' }}>
                    {emptyLabel}
                  </TableCell>
                </TableRow>
              )}
              {invitations.map((invitation) => (
                <InvitationTableRow key={invitation.id} invitation={invitation} columns={columns} />
              ))}
            </TableBody>
          )}
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(event, newPage) => onPageChange(newPage)}
        onRowsPerPageChange={(event) => onRowsPerPageChange(parseInt(event.target.value, 10))}
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
      />
    </Paper>
  );
}
